import { CircleArrowUpIcon, PlusCircleIcon, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useRef, useState } from 'react';
import { Button } from 'components/shadcn/button';
import { useTranslation } from 'react-i18next';
import { cn } from 'utils';
import { Textarea } from 'components/shadcn/textarea';
import { Badge } from '@/components/shadcn/badge';
import { toast } from 'sonner';

const MAX_FILES = 5 as const;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'] as const;

export const UserInput = ({
  isLoading,
  label,
  canAcceptMediaMessages,
  onSend,
  dir,
  customRingColor,
}: {
  customRingColor?: string;
  isLoading?: boolean;
  canAcceptMediaMessages: boolean;
  label?: string;
  dir?: 'ltr' | 'rtl';
  onSend: (text: string, fileList: File[]) => Promise<void>;
}) => {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [fileList, setFileList] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const isIframe = window.self !== window.top;
  const isMobile = isIframe ? false : useIsMobile();

  const isSubmitDisabled = (!text && !fileList.length) || isLoading;

  const handleSubmit = () => {
    if (!isSubmitDisabled) {
      onSend(convertToParagraph(text), fileList);
      setFileList([]);
      setText('');
    }
  };

  // Allow Shift+Enter to add a new line, and make sure newline does not send on mobile
  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      handleSubmit();
    }
  };

  // Prevent lone Enter from adding new line on submit
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !isMobile) {
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // @ts-ignore-next-line
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(t('common.invalidFileType', { userType: file.type }));
      return;
    }

    if (fileList.length >= MAX_FILES) {
      toast.error(t('common.maxFilesReached', { maxFiles: MAX_FILES }));
      return;
    }
    setFileList((prev) => [...prev, file]);
    e.target.value = '';
    inputRef.current?.focus();
  };

  const removeFile = (index: number) => {
    setFileList((prev) => prev.filter((_e, i) => i !== index));
  };

  return (
    <div>
      <div className="flex flex-row-reverse py-4 gap-2 flex-wrap">
        {fileList.map((e, index) => (
          <Badge className="flex gap-2 select-none" variant={'outline'} key={e.name + index}>
            <Button
              disabled={isLoading || fileList.length >= MAX_FILES}
              className="w-fit h-fit py-0 px-1 rounded-full"
              variant={'ghost'}
              onClick={() => {
                removeFile(index);
              }}
            >
              <X className="w-3 p-0 m-0" />
            </Button>
            <p className="pe-2">{e.name}</p>
          </Badge>
        ))}
      </div>
      <div className="relative flex">
        {canAcceptMediaMessages && (
          <>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              disabled={isLoading}
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 rounded-full text-gray-500 hover:text-secondary hover:bg-transparent"
              aria-label="Attach file"
            >
              <PlusCircleIcon className="h-4 w-4" />
            </Button>
          </>
        )}

        <Textarea
          rows={1}
          id="user-input"
          style={
            customRingColor ? ({ '--ring': customRingColor } as React.CSSProperties) : undefined
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyUp={handleKeyUp}
          onKeyDown={onKeyDown}
          placeholder={label}
          ref={inputRef}
          className="pe-10 min-h-10 resize-none"
        />

        <Button
          onMouseDown={(e) => e.preventDefault()} // Note: this prevents the keyboard from going down on mobile
          size="icon"
          variant="ghost"
          asChild={false}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          className={cn(
            'absolute top-1/2 transform -translate-y-1/2 text-gray-500 z-1',
            dir === 'ltr' ? 'right-0' : 'left-0',
            isLoading && 'opacity-60 animate-pulse',
            'rounded-full hover:text-secondary hover:bg-transparent active:scale-[0.96] transition-[transform,opacity]',
          )}
        >
          <span className="sr-only">{t('common.send')}</span>
          <CircleArrowUpIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ReactMarkdown is expecting \n\n for a new paragraph. one \n is beeing ignored.
function convertToParagraph(input: string): string {
  return input.replace(/\n/g, '\n\n');
}
